import React, { useState, useCallback, useEffect, useMemo } from "react";
import { EyeOutlined, MobileOutlined, DesktopOutlined, SettingOutlined } from "@ant-design/icons";
import { Radio } from "antd";
import '../../styles/operator-website-page-config.style.scss';
import { OperatorWebsiteConfigComponent } from "../../components/operator-website-config-component/web-root.component";
import { OperatorWebsitePreview } from "../../components/operator-website-config-component/operator-website-preview.component";
import { getWebConfig } from "../../actions/operator-web-config.action";
import { getWebSiteUrlForStorage } from "../../utils/constant";

const GCS_BASE_URL = 'https://storage.googleapis.com/operator-websites';
const NARROW_BREAKPOINT = 1024;

export const OperatorWebsitePageConfig = (props) => {
    const [activeTab, setActiveTab] = useState('images');
    const [websiteColors, setWebsiteColors] = useState({});
    const [websiteTexts, setWebsiteTexts] = useState({});
    const [websiteConfig, setWebsiteConfig] = useState([]);
    const [webConfigActions, setWebConfigActions] = useState({});
    const [imagePreviewOverrides, setImagePreviewOverrides] = useState({});
    const [previewView, setPreviewView] = useState('desktop');
    const [mobileMainTab, setMobileMainTab] = useState('configure');
    const [isNarrowView, setIsNarrowView] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW_BREAKPOINT);

    useEffect(() => {
        const onResize = () => setIsNarrowView(window.innerWidth < NARROW_BREAKPOINT);
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        getWebConfig((data) => {
            if (data?.webConfigColors) {
                setWebsiteColors(data.webConfigColors);
            }
            if (data?.webActions) {
                setWebConfigActions(data.webActions);
            }
            if (data?.webMapping) {
                setWebsiteConfig(data.webMapping);
            }
        }, () => {});
    }, []);

    const onColorsChange = useCallback((values) => {
        setWebsiteColors((prev) => ({ ...prev, ...values }));
    }, []);

    const onTextsChange = useCallback((values) => {
        setWebsiteTexts((prev) => ({ ...prev, ...values }));
    }, []);

    const onConfigChange = useCallback((values) => {
        setWebsiteConfig((prev) => ({ ...prev, ...values }));
    }, []);

    const imageBaseUrl = useMemo(() => {
        const base = getWebSiteUrlForStorage();
        return base ? `${GCS_BASE_URL}/${base}` : '';
    }, []);

    const onPreviewImageOverridesChange = useCallback((overrides) => {
        setImagePreviewOverrides(overrides || {});
    }, []);

    const configPanel = (
        <OperatorWebsiteConfigComponent
            activeTab={activeTab}
            onTabChange={setActiveTab}
            websiteColors={websiteColors}
            websiteTexts={websiteTexts}
            websiteConfig={websiteConfig}
            onColorsChange={onColorsChange}
            onTextsChange={onTextsChange}
            onConfigChange={onConfigChange}
            webConfigActions={webConfigActions}
            onPreviewImageOverridesChange={onPreviewImageOverridesChange}
        />
    );

    const previewPane = (
        <div className="operator-website-preview-pane">
            <div className="operator-website-preview-header">
                <span><EyeOutlined /> Live preview</span>
                <Radio.Group
                    size="small"
                    value={previewView}
                    onChange={(e) => setPreviewView(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="desktop"><DesktopOutlined /> Desktop</Radio.Button>
                    <Radio.Button value="mobile"><MobileOutlined /> Mobile</Radio.Button>
                </Radio.Group>
            </div>
            <div className="operator-website-preview-scroll">
                <div className={previewView === 'mobile' ? 'operator-website-preview-viewport operator-website-preview-viewport--mobile' : 'operator-website-preview-viewport'}>
                    <OperatorWebsitePreview
                        websiteColors={websiteColors}
                        imageBaseUrl={imageBaseUrl}
                        imagePreviewOverrides={imagePreviewOverrides}
                        variant={previewView}
                    />
                </div>
            </div>
        </div>
    );

    if (isNarrowView) {
        return (
            <div className="operator-website-page-config-root operator-website-page-config-root--narrow">
                <div className="operator-website-mobile-tabs">
                    <button
                        type="button"
                        className={`operator-website-mobile-tab${mobileMainTab === 'configure' ? ' operator-website-mobile-tab--active' : ''}`}
                        onClick={() => setMobileMainTab('configure')}
                    >
                        <SettingOutlined /> Configure
                    </button>
                    <button
                        type="button"
                        className={`operator-website-mobile-tab${mobileMainTab === 'preview' ? ' operator-website-mobile-tab--active' : ''}`}
                        onClick={() => setMobileMainTab('preview')}
                    >
                        <EyeOutlined /> Preview
                    </button>
                </div>
                <div className="operator-website-mobile-content">
                    {mobileMainTab === 'configure' ? (
                        <div className="operator-website-config-pane">{configPanel}</div>
                    ) : (
                        previewPane
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="operator-website-page-config-root operator-website-page-config-root--split">
            <div className="operator-website-config-pane">
                {configPanel}
            </div>
            {previewPane}
        </div>
    );
};
